import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ModuleId } from '@prody/shared';

/* ------------------------------------------------------------------ *
 * Per-user navigation preferences.
 *
 * Favourites are keyed by business slug: the same person runs a
 * wholesaler and a farm, and "what I use daily" differs per tenant.
 * Pin ORDER is preserved — the list is the user's own priority, not
 * the registry's.
 *
 * Persistence is localStorage, which is per browser profile — i.e.
 * per user on their machine. When the real API lands, this store is
 * the single place to add server sync; nothing else changes.
 *
 * Stale ids (a module later disabled for the tenant, or a role
 * losing access) are kept in storage but filtered out at render
 * against the resolved registry — so re-enabling a module brings a
 * pin back instead of silently losing it.
 * ------------------------------------------------------------------ */

interface NavPrefsState {
  favorites: Record<string, ModuleId[]>;
  toggleFavorite: (businessSlug: string, id: ModuleId) => void;
}

export const useNavPrefs = create<NavPrefsState>()(
  persist(
    (set) => ({
      favorites: {},
      toggleFavorite: (slug, id) =>
        set((s) => {
          const current = s.favorites[slug] ?? [];
          const next = current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id];
          return { favorites: { ...s.favorites, [slug]: next } };
        }),
    }),
    { name: 'prody.nav' },
  ),
);

/** Stable empty list so selectors don't re-render on every store change. */
export const NO_FAVORITES: ModuleId[] = [];
